import { DocumentNode, gql } from '@apollo/client';
import {
  Box,
  HStack,
  Hide,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Td,
  Text,
  Tooltip,
  Tr,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import * as React from 'react';

import { DeleteLibraryResourceModal } from '@/components/LibraryResourceModals/DeleteLibraryResourceModal';
import { RenameLibraryResourceModal } from '@/components/LibraryResourceModals/RenameLibraryResourceModal';
import { TruncatedItemName } from '@/components/TruncatedItemName';
import {
  Edit2Icon,
  FolderIcon,
  MoreHorizontalIcon,
  MoveIcon,
  TrashIcon,
} from '@/imports/ui/chakra/feather';
import { MoveLibraryFolderModal } from '@/imports/ui/components/resource/MoveLibraryFolderModal';

import { ResourceListFolder_LibraryFolderFragment } from './graphql-types/ResourceListFolder_LibraryFolderFragment';

const FolderItemDropdown: React.FC<{
  isDeletable: boolean;
  onClickMoveFolder: () => void;
  onClickDeleteFolder: () => void;
  onClickEdit: () => void;
}> = ({ isDeletable, onClickEdit, onClickMoveFolder, onClickDeleteFolder }) => {
  return (
    <Menu aria-label="folder actions">
      <MenuButton
        as={IconButton}
        variant="outline"
        colorScheme="gray"
        icon={<MoreHorizontalIcon />}
        size="sm"
      />
      <Portal>
        <MenuList>
          <MenuItem
            icon={<MoveIcon color="gray.600" />}
            onClick={onClickMoveFolder}
          >
            Move
          </MenuItem>
          {isDeletable && (
            <MenuItem
              icon={<TrashIcon color="gray.600" />}
              onClick={onClickDeleteFolder}
            >
              Delete
            </MenuItem>
          )}
          <MenuItem icon={<Edit2Icon color="gray.600" />} onClick={onClickEdit}>
            Rename
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
};

type IResourceListFolderProps = {
  folder: ResourceListFolder_LibraryFolderFragment;
  rootFolderId: string;
  navigateToFolder: (folderId: string) => void;
  onAfterMove: () => void;
  currentUserCanEditLibrary: boolean;
};

export const ResourceListFolder: React.FC<IResourceListFolderProps> & {
  folderFragment: DocumentNode;
} = ({
  folder,
  rootFolderId,
  navigateToFolder,
  onAfterMove,
  currentUserCanEditLibrary,
}) => {
  const renameFolderModal = useDisclosure();
  const deleteFolderModal = useDisclosure();
  const moveFolderModal = useDisclosure();

  const lineLength = useBreakpointValue({
    base: 20,
    sm: 40,
    md: 40,
    lg: 20,
    xl: 55,
  });

  if (!folder?.name) return null;

  return (
    <Tr _hover={{ bg: 'gray.50' }} height="4em">
      <MoveLibraryFolderModal
        rootFolderId={rootFolderId}
        libraryFolderId={folder._id}
        open={moveFolderModal.isOpen}
        onClose={moveFolderModal.onClose}
        onAfterMove={onAfterMove}
      />

      <DeleteLibraryResourceModal
        onClose={deleteFolderModal.onClose}
        isOpen={deleteFolderModal.isOpen}
        libraryFolderId={folder._id}
      />

      <RenameLibraryResourceModal
        onClose={renameFolderModal.onClose}
        isOpen={renameFolderModal.isOpen}
        libraryFolderId={folder._id}
        initialName={folder.name}
      />
      {currentUserCanEditLibrary && <Td />}
      <Td>
        <Tooltip
          hasArrow
          label={folder.name}
          isDisabled={!!lineLength && folder.name.length <= lineLength}
        >
          <HStack
            _hover={{ cursor: 'pointer' }}
            onClick={() => navigateToFolder(folder._id)}
          >
            <FolderIcon fill="currentcolor" boxSize={6} />
            <TruncatedItemName
              name={folder.name}
              minEndChars={5}
              numOfLines={1}
              maxLineLength={lineLength}
              textStyle="compactStrong"
            />
          </HStack>
        </Tooltip>
      </Td>
      <Hide below="lg">
        <Td>
          <Text textStyle="compact">
            {dayjs(folder.dateCreated).format('MMM D, YYYY')}
          </Text>
        </Td>
        <Td />
      </Hide>
      <Td>
        {currentUserCanEditLibrary && (
          <FolderItemDropdown
            isDeletable={folder.isDeletable}
            onClickMoveFolder={moveFolderModal.onOpen}
            onClickDeleteFolder={deleteFolderModal.onOpen}
            onClickEdit={renameFolderModal.onOpen}
          />
        )}
      </Td>
    </Tr>
  );
};

ResourceListFolder.folderFragment = gql`
  fragment ResourceListFolder_LibraryFolderFragment on LibraryFolder {
    _id
    name
    dateCreated
    isDeletable
  }
`;
